import { useConferirDia, useRemoverDia } from '../../../api/queries/referenciasAdmin'
import { Botao } from '../../../components/Botao'
import { cn } from '../../../lib/cn'
import { data as formatarData } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { DiaNaoUtil } from '../../../types/usuario'

const t = TEXTOS.referencias.calendario

const SELO =
  'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset'

/**
 * Um dia do calendário, com as duas ações que ele admite.
 *
 * **A ação que faltava era a de conferir.** A tela listava "ainda não conferido" ao lado de cada
 * dia e oferecia só "Remover" — de modo que a única saída visível de um estado que trava o TIS era
 * apagar o feriado. O endpoint (`PATCH /admin/referencias/dias-nao-uteis/{data}`) existia desde o
 * começo; o botão é que nunca foi ligado.
 *
 * @param dia o dia a exibir
 * @returns a linha da lista
 */
export function LinhaDoDiaNaoUtil({ dia }: { dia: DiaNaoUtil }) {
  const conferir = useConferirDia()
  const remover = useRemoverDia()

  return (
    <li className="flex flex-wrap items-center gap-3 py-2.5">
      <span className="w-24 shrink-0 text-sm tabular-nums text-slate-900">{formatarData(dia.data)}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-slate-800">{dia.rotulo}</span>
        <span className="block text-xs text-slate-500">{rotuloDaAbrangencia(dia.abrangencia)}</span>
      </span>

      {dia.conferido ? (
        <span className={cn(SELO, 'bg-green-50 text-green-900 ring-green-200')}>
          {t.conferido}
          {dia.conferidoPorNome ? ` · ${dia.conferidoPorNome}` : ''}
        </span>
      ) : (
        <>
          <span className={cn(SELO, 'bg-amber-50 text-amber-900 ring-amber-200')}>{t.naoConferido}</span>
          <Botao
            tamanho="pequeno"
            aparencia="primario"
            onClick={() => conferir.mutate(dia.data)}
            disabled={conferir.isPending}
          >
            {conferir.isPending ? t.conferindo : t.conferir}
          </Botao>
        </>
      )}

      <Botao tamanho="pequeno" onClick={() => remover.mutate(dia.data)} disabled={remover.isPending}>
        {t.remover}
      </Botao>
    </li>
  )
}

/** As quatro abrangências por extenso — `MUNICIPAL` na tela é ruído para quem não é da área. */
function rotuloDaAbrangencia(abrangencia: string) {
  const rotulos: Record<string, string> = {
    MUNICIPAL: 'Feriado municipal',
    ESTADUAL: 'Feriado estadual',
    NACIONAL: 'Feriado nacional',
    PONTO_FACULTATIVO: 'Ponto facultativo',
  }
  return rotulos[abrangencia] ?? abrangencia
}
